// RBAC Route Protection Component
// Date: January 13, 2025
// Purpose: Protect routes based on RBAC navigation screen permissions
// This component checks if user has READ permission for the navigation screen
// associated with the route path before allowing access

import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/state/auth/AuthProvider';
import { unifiedRBACService } from '@/services/unifiedRBACService';
import { useLanguage } from '@/state/language/LanguageProvider';
import { getScreenCodeForRoute, shouldProtectRoute } from '@/config/routeProtection';
import { toast } from 'react-hot-toast';

interface RBACRouteProps {
  children: React.ReactNode;
  /**
   * Route path (e.g., '/actions', '/admin/maintenance')
   * Used to find the navigation screen that controls access
   * If not provided, will use current location pathname
   */
  routePath?: string;
  /**
   * Optional: Screen code if route path doesn't match exactly
   * (e.g., if route is '/actions' but screen code is 'ACTION_MANAGEMENT')
   * If not provided, will look up from routeToScreenMap
   */
  screenCode?: string;
  /**
   * Optional: Fallback route if access denied (default: '/dashboard')
   * NOTE: Currently unused - we show access denied UI instead of redirecting
   */
  fallbackRoute?: string;
  /**
   * Optional: Show loading state while checking permissions
   */
  showLoading?: boolean;
  /**
   * Optional: Auto-detect route path and screen code from location
   * If true, routePath and screenCode are optional
   */
  autoDetect?: boolean;
}

/**
 * RBACRoute Component
 * 
 * Protects routes by checking if the user has READ permission
 * for the navigation screen associated with the route path.
 * 
 * Security Flow:
 * 1. User must be authenticated (handled by ProtectedRoute wrapper)
 * 2. Component looks up navigation screen by route_path
 * 3. Checks if user has READ permission on that screen
 * 4. If no permission: Shows access denied message and UI (DO NOT REDIRECT)
 * 5. If permission exists: Renders children
 * 
 * Platform Administrators automatically have access to all screens.
 */
export const RBACRoute: React.FC<RBACRouteProps> = ({
  children,
  routePath: providedRoutePath,
  screenCode: providedScreenCode,
  fallbackRoute: _fallbackRoute = '/dashboard', // Unused - kept for backward compatibility
  showLoading = true,
  autoDetect = false
}) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [checkingPermission, setCheckingPermission] = useState(true);
  const [hasShownError, setHasShownError] = useState(false);

  // Auto-detect route path and screen code if autoDetect is true
  const routePath = providedRoutePath || (autoDetect ? location.pathname : '');
  const currentPath = routePath || location.pathname;
  
  // Special handling for module paths - ModulePage handles its own permissions
  // This check happens BEFORE any screen code lookup to ensure module paths are always allowed
  const isModulePath = currentPath.startsWith('/modules/');
  
  // For module paths, don't look up screen code - ModulePage handles permissions
  const screenCode = isModulePath ? undefined : (providedScreenCode || (autoDetect ? getScreenCodeForRoute(currentPath) || undefined : undefined));

  useEffect(() => {
    // Reset error flag when route changes
    setHasShownError(false);
    
    const checkPermission = async () => {
      // Wait for auth to complete
      if (authLoading) {
        return;
      }
      
      // Special handling for module paths - ModulePage handles its own permissions
      // This allows modules to be moved around in navigation without code changes
      if (isModulePath) {
        setHasPermission(true);
        setCheckingPermission(false);
        return;
      }

      // Must be authenticated
      if (!isAuthenticated || !user?.id) {
        setHasPermission(false);
        setCheckingPermission(false);
        return;
      }

      // Check if route should be protected by frontend RBAC
      // Note: Module paths are always allowed - ModulePage handles its own permissions
      if (!shouldProtectRoute(currentPath)) {
        setHasPermission(true);
        setCheckingPermission(false);
        return;
      }

      // If no route path provided and auto-detect is off, deny access
      if (!routePath && !autoDetect) {
        setHasPermission(false);
        setCheckingPermission(false);
        return;
      }

      try {
        setCheckingPermission(true);

        // SIMPLE RULE: Only protect routes that are registered in the navigation system
        // If route is NOT in navigation system → Allow access (backend handles it, or it's public, or new route)
        // If route IS in navigation system → Check READ permission → Deny if no permission
        // Module paths are NEVER checked here - they're handled by ModulePage

        // Step 1: Check if navigation screen exists for this route
        // IMPORTANT: Skip this check for module paths - they don't have screens, they have modules
        let screenExists = false;
        let existingScreenCode: string | undefined = undefined;

        if (!isModulePath) {
          // Only check for screens if this is NOT a module path
          try {
            const screenCheck = await unifiedRBACService.checkScreenExists(
              currentPath,
              screenCode
            );
            screenExists = screenCheck.exists;
            existingScreenCode = screenCheck.screenCode;
            
            // If screen is marked as public, allow access immediately
            if (screenCheck.exists && screenCheck.isPublic) {
              setHasPermission(true);
              setCheckingPermission(false);
              return;
            }
          } catch (error: any) {
            // If check fails, assume screen doesn't exist (fail closed for security)
            screenExists = false;
          }
        }

        // Step 2: If screen exists in navigation system, check permission
        // If screen NOT registered in DB → Deny access (fail closed - screen must be registered)
        // Note: Public routes are handled by shouldProtectRoute() and never reach this point
        // Note: Screens marked as is_public=true in DB are allowed above
        if (screenExists && existingScreenCode) {
          // Screen is registered in navigation system → Check READ permission
          // Backend will handle platform admin check and return hasPermission accordingly
          try {
            const hasPermission = await unifiedRBACService.checkScreenPermissionByCode(
              user.id,
              existingScreenCode,
              'READ'
            );
            setHasPermission(hasPermission);
          } catch (error: any) {
            console.error('[RBACRoute] Error checking permission for screen:', existingScreenCode, error);
            // On error, deny access (fail closed for security)
            setHasPermission(false);
          }
        } else {
          // Screen NOT registered in navigation system (not found in DB) → Deny access (fail closed)
          // Screens must be registered in navigation_screens table before they can be accessed
          // This ensures all protected routes are properly configured in the navigation system
          console.warn('[RBACRoute] Screen not registered in navigation system:', {
            routePath: currentPath,
            screenCode,
            screenExists,
            existingScreenCode
          });
          setHasPermission(false);
        }
      } catch (error) {
        console.error('[RBACRoute] Error checking permission:', error);
        // On error, deny access (fail closed for security)
        setHasPermission(false);
      } finally {
        setCheckingPermission(false);
      }
    };

    checkPermission();
  }, [isAuthenticated, authLoading, user?.id, routePath, screenCode, location.pathname]);

  // Show loading state while checking
  if (authLoading || checkingPermission) {
    if (showLoading) {
      return (
        <div className="min-h-screen bg-[var(--color-surface-bg)] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-[var(--color-secondary-text)]">
              {t('common.loading') || 'Loading...'}
            </p>
          </div>
        </div>
      );
    }
    return null;
  }

  // Not authenticated - should be handled by ProtectedRoute, but fail safe
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // No permission - show access denied message (DO NOT REDIRECT)
  // Only show error if permission is explicitly false
  if (hasPermission === false) {
    // Show error message to user (only once)
    if (!hasShownError) {
      toast.error(t('navigation.accessDenied') || 'You do not have permission to access this page. Please contact your administrator if you believe this is an error.', {
        duration: 5000,
        id: 'access-denied', // Use same ID to prevent duplicates
      });
      setHasShownError(true);
    }
    // Show access denied UI instead of redirecting
    return (
      <div className="min-h-screen bg-[var(--color-surface-bg)] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="mb-6">
            <svg
              className="mx-auto h-16 w-16 text-[var(--color-error)]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-[var(--color-primary-text)] mb-2">
            {t('navigation.accessDenied') || 'Access Denied'}
          </h1>
          <p className="text-[var(--color-secondary-text)] mb-6">
            {t('navigation.accessDeniedMessage') || 'You do not have permission to access this page. Please contact your administrator if you believe this is an error.'}
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] rounded hover:opacity-90 transition-opacity"
          >
            {t('common.goBack') || 'Go Back'}
          </button>
        </div>
      </div>
    );
  }
  
  // Reset error flag when permission is granted
  if (hasPermission === true && hasShownError) {
    setHasShownError(false);
  }

  // If permission is null after checking completes, something went wrong
  // Allow access (fail open) to prevent blocking legitimate users
  // This should rarely happen - if it does, investigate the permission check logic
  if (hasPermission === null && !checkingPermission) {
    return <>{children}</>;
  }

  // Has permission (explicitly true) or still checking - render children
  return <>{children}</>;
};

